/**
 * Custom hook untuk update restriction rate & avail berdasarkan tanggal dan rate ID.
 * 
 * API Endpoint: POST `/api/v4/update-availability-restriction`
 * API Version: 4.0
 * 
 * @param {array} values - Array of ValueResponse
 * @returns {object} - Return value berupa object dengan key `data`, `isLoading`, dan `error`.
 * @example
 * const { mutate, isPending } = useUpdateAvailabilityRestriction();
 */

import { useMutation } from "@tanstack/react-query";
import { privateApi } from "../../../api/api";
import { ValueResponse } from "../schema/UpdateAvailabilityRestrictionSchema";
import { UpdateAvailabilityRestrictionSchema } from "../response/UpdateAvailabilityRestrictionResponse";
import { ValidationErrorResponse } from "../../common/response/ValidationErrorResponse";
import { z } from "zod";
import { ChannexValidationErrorResponse } from "../response/ChannexValidationErrorResponse";

const API_VERSION = 'v4';

export const useUpdateAvailabilityRestriction = () => {
    return useMutation({
        mutationFn: async (data: {
            values: ValueResponse[];
        }) => privateApi.post(`/${API_VERSION}/update-availability-restriction`, data),
        onSuccess: async (response) => {
            try {
                const validationResult = UpdateAvailabilityRestrictionSchema.safeParse(response.data);

                if (!validationResult.success) {
                    console.error("Validation failed:", validationResult.error.errors);
                    throw new Error("Invalid update restriction data format");
                }

                return validationResult.data;
            } catch (error) {
                console.error('Failed to update restriction', error);
                throw error;
            }
        },
        // onError: (error) => {
        //     const validationResult = ValidationErrorResponse.safeParse((error as any).response.data);

        //     if (!validationResult.success) {
        //         console.error("Validation failed:", validationResult.error.errors);
        //         throw new Error("Invalid restriction data format");
        //     } else {
        //         console.error('Failed to update restriction', error);
        //         throw error;
        //     }
        // },
        onError: (error) => {
            const rawData = (error as any)?.response?.data;

            const baseResult = z.object({ message: z.string() }).safeParse(rawData);

            if (baseResult.success && baseResult.data.message === "channex_validation_error") {
                const channexResult = ChannexValidationErrorResponse.safeParse(rawData);

                if (!channexResult.success) {
                    console.error("Channex validation structure invalid:", channexResult.error.errors);
                    throw new Error("Invalid Channex restriction data format");
                }

                console.error("Channex validation error:", channexResult.data);
                throw new Error("Channex validation error occurred");
            }

            const validationResult = ValidationErrorResponse.safeParse(rawData);

            if (!validationResult.success) {
                console.error("General validation failed:", validationResult.error.errors);
                throw new Error("Invalid general validation data format");
            }

            console.error("General validation error:", validationResult.data);
            throw new Error("General validation error occurred");
        }
    });
};